import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Search, X } from 'lucide-react';
import { Badge } from './ui/badge';

interface FilterSuggestion {
  id: number;
  value: string;
  category_key: string;
}

interface CategoryGroup {
  category: {
    id: number;
    name: string;
    key: string;
  };
  values: FilterSuggestion[];
}

interface FilterSearchAutocompleteProps {
  placeholder?: string;
  onSelect?: (suggestion: FilterSuggestion) => void;
  onRemove?: (suggestion: FilterSuggestion) => void;
  selectedFilters?: FilterSuggestion[];
  className?: string;
  initialValue?: string;
}

const FilterSearchAutocomplete: React.FC<FilterSearchAutocompleteProps> = ({
  placeholder = "Rechercher une spécialité, zone du corps, équipement...",
  onSelect,
  onRemove,
  selectedFilters: externalSelectedFilters,
  className = "",
  initialValue = ""
}) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<CategoryGroup[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  
  // Utiliser les filtres externes si fournis, sinon gérer localement
  const selectedFilters = externalSelectedFilters || [];

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/filter_values/search?q=${encodeURIComponent(query)}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
          setIsOpen(data.length > 0);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (suggestion: FilterSuggestion) => {
    // Vérifier si le filtre n'est pas déjà sélectionné
    if (!selectedFilters.find(f => f.id === suggestion.id)) {
      if (onSelect) {
        onSelect(suggestion);
      }
    }
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const removeFilter = (filter: FilterSuggestion) => {
    if (onRemove) {
      onRemove(filter);
    }
  };

  const getCategoryColor = (categoryKey: string) => {
    const colors: Record<string, string> = {
      'SPECIALTIES': 'bg-blue-100 text-blue-800',
      'BODY_ZONES': 'bg-green-100 text-green-800',
      'EQUIPMENT': 'bg-purple-100 text-purple-800',
      'ACCESSIBILITY': 'bg-yellow-100 text-yellow-800',
      'LANGUAGES': 'bg-pink-100 text-pink-800',
      'TECHNIQUES': 'bg-indigo-100 text-indigo-800',
      'PRACTICE_LOCATIONS': 'bg-teal-100 text-teal-800',
      'PATIENT_TYPES': 'bg-orange-100 text-orange-800',
      'PAYMENT_METHODS': 'bg-cyan-100 text-cyan-800',
      'APPOINTMENT_METHODS': 'bg-emerald-100 text-emerald-800'
    };
    return colors[categoryKey] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div ref={wrapperRef} className={`${className}`}>
      {/* Input + dropdown anchored together */}
      <div className="relative">
        <div className="relative h-10">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setIsOpen(true);
            }}
            placeholder={placeholder}
            className="pl-10 pr-10 h-10"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#008788]"></div>
            </div>
          )}
        </div>

        {/* Dropdown de suggestions – anchored to input, not the full wrapper */}
        {isOpen && suggestions.length > 0 && (
          <div className="absolute z-50 w-full top-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {suggestions.map((group) => (
              <div key={group.category.key} className="border-b last:border-b-0">
                <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {group.category.name}
                </div>
                <div className="py-1">
                  {group.values.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => handleSelect(suggestion)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between group"
                    >
                      <span className="text-sm text-gray-700">{suggestion.value}</span>
                      <Badge
                        variant="secondary"
                        className={`${getCategoryColor(suggestion.category_key)} opacity-0 group-hover:opacity-100 transition-opacity text-xs`}
                      >
                        {group.category.name}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message si pas de résultats */}
        {isOpen && query.length >= 2 && suggestions.length === 0 && !isLoading && (
          <div className="absolute z-50 w-full top-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
            Aucun résultat trouvé pour "{query}"
          </div>
        )}
      </div>

      {/* Filtres sélectionnés – en dehors du contexte positionné du dropdown */}
      {selectedFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedFilters.map((filter) => (
            <Badge
              key={filter.id}
              variant="secondary"
              className={`${getCategoryColor(filter.category_key)} flex items-center gap-1`}
            >
              {filter.value}
              <button
                type="button"
                onClick={() => removeFilter(filter)}
                className="ml-1 hover:bg-black/10 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterSearchAutocomplete;

